import type { BrowserLiveViewStatus } from '@auw/workspace-utils';

export interface ConversationRuntimeLiveViewState {
  liveViewRef: string | null;
  available: boolean;
  running: boolean;
  open: boolean;
  watchOnly: boolean;
  state: BrowserLiveViewStatus | null;
  errorMessage: string | null;
}

export interface ConversationRuntimeState {
  abortController: AbortController | null;
  streamingSessionId: string | null;
  streamingMessageId: string | null;
  streamingWorkspaceType: string | null;
  isUploading: boolean;
  liveView: ConversationRuntimeLiveViewState;
}

export type ConversationRuntimeMap = Record<string, ConversationRuntimeState>;

export const EMPTY_CONVERSATION_LIVE_VIEW_RUNTIME: ConversationRuntimeLiveViewState = {
  liveViewRef: null,
  available: false,
  running: false,
  open: false,
  watchOnly: true,
  state: null,
  errorMessage: null,
};

export const EMPTY_CONVERSATION_RUNTIME: ConversationRuntimeState = {
  abortController: null,
  streamingSessionId: null,
  streamingMessageId: null,
  streamingWorkspaceType: null,
  isUploading: false,
  liveView: EMPTY_CONVERSATION_LIVE_VIEW_RUNTIME,
};

export function isConversationLiveViewRuntimeIdle(
  runtime: ConversationRuntimeLiveViewState | null | undefined,
): boolean {
  return (
    runtime?.liveViewRef == null
    && runtime?.available !== true
    && runtime?.running !== true
    && runtime?.open !== true
    && runtime?.state == null
    && runtime?.errorMessage == null
  );
}

export function hasConversationAbortController(
  runtime: ConversationRuntimeState | null | undefined,
): boolean {
  return runtime?.abortController != null;
}

export function isConversationRuntimeStreaming(
  runtime: ConversationRuntimeState | null | undefined,
): boolean {
  return hasConversationAbortController(runtime);
}

export function isConversationRuntimeBusy(
  runtime: ConversationRuntimeState | null | undefined,
): boolean {
  return isConversationRuntimeStreaming(runtime) || runtime?.isUploading === true;
}

export function isConversationRuntimeIdle(runtime: ConversationRuntimeState): boolean {
  return (
    runtime.abortController === null
    && runtime.streamingSessionId === null
    && runtime.streamingMessageId === null
    && runtime.streamingWorkspaceType === null
    && !runtime.isUploading
    && isConversationLiveViewRuntimeIdle(runtime.liveView)
  );
}

export function getConversationRuntime(
  runtimes: ConversationRuntimeMap,
  conversationId?: string | null,
): ConversationRuntimeState | null {
  if (!conversationId) return null;
  return runtimes[conversationId] ?? null;
}

export function updateConversationRuntimeMap(
  runtimes: ConversationRuntimeMap,
  conversationId: string | null | undefined,
  updater: (prev: ConversationRuntimeState) => ConversationRuntimeState,
): ConversationRuntimeMap {
  if (!conversationId) return runtimes;
  const prev = runtimes[conversationId] ?? EMPTY_CONVERSATION_RUNTIME;
  const next = updater(prev);
  const unchanged = (
    prev.abortController === next.abortController
    && prev.streamingSessionId === next.streamingSessionId
    && prev.streamingMessageId === next.streamingMessageId
    && prev.streamingWorkspaceType === next.streamingWorkspaceType
    && prev.isUploading === next.isUploading
    && prev.liveView.liveViewRef === next.liveView.liveViewRef
    && prev.liveView.available === next.liveView.available
    && prev.liveView.running === next.liveView.running
    && prev.liveView.open === next.liveView.open
    && prev.liveView.watchOnly === next.liveView.watchOnly
    && prev.liveView.state === next.liveView.state
    && prev.liveView.errorMessage === next.liveView.errorMessage
  );

  if (isConversationRuntimeIdle(next)) {
    if (!(conversationId in runtimes)) return runtimes;
    const { [conversationId]: _removed, ...rest } = runtimes;
    return rest;
  }

  if (unchanged && conversationId in runtimes) {
    return runtimes;
  }

  return {
    ...runtimes,
    [conversationId]: next,
  };
}

export function patchConversationRuntimeMap(
  runtimes: ConversationRuntimeMap,
  conversationId: string | null | undefined,
  patch: Partial<ConversationRuntimeState>,
): ConversationRuntimeMap {
  return updateConversationRuntimeMap(
    runtimes,
    conversationId,
    (prev) => ({ ...prev, ...patch }),
  );
}

export function clearConversationRuntimeMap(
  runtimes: ConversationRuntimeMap,
  conversationId: string | null | undefined,
): ConversationRuntimeMap {
  if (!conversationId || !(conversationId in runtimes)) return runtimes;
  const { [conversationId]: _removed, ...rest } = runtimes;
  return rest;
}
